/**
 * Commitment layer — one shared vocabulary for BailBuddies' lock-in lifecycle.
 *
 * The database keeps its existing status strings ("pending" | "going" |
 * "bailed", plus the legacy "accepted"/"maybe"/"declined"/"cant" values). This
 * module maps those to the commitment-first UX language used everywhere:
 * Pending / Locked In / Bailed.
 */

export type CommitmentKey = "locked_in" | "bailed" | "maybe" | "declined" | "pending";

export interface CommitmentLabel {
  key: CommitmentKey;
  /** Short label used in lists, chips and summaries. */
  label: string;
  /** Longer, friendly one-liner. */
  description: string;
  emoji: string;
}

const LABELS: Record<CommitmentKey, CommitmentLabel> = {
  locked_in: {
    key: "locked_in",
    label: "Locked In",
    description: "Committed to showing up",
    emoji: "🔒",
  },
  bailed: {
    key: "bailed",
    label: "Bailed",
    description: "Stepped back — privately, no judgment",
    emoji: "👻",
  },
  maybe: {
    key: "maybe",
    label: "Maybe",
    description: "Still deciding",
    emoji: "🤔",
  },
  declined: {
    key: "declined",
    label: "Can't Make It",
    description: "Not able to come",
    emoji: "🙅",
  },
  pending: {
    key: "pending",
    label: "Pending",
    description: "Hasn't responded yet",
    emoji: "⏳",
  },
};

/** Map any stored invite/invitation status to its commitment label. */
export function commitmentStatus(status?: string | null): CommitmentLabel {
  switch (status) {
    case "going":
    case "accepted":
      return LABELS.locked_in;
    case "bailed":
      return LABELS.bailed;
    case "maybe":
      return LABELS.maybe;
    case "declined":
    case "cant":
      return LABELS.declined;
    default:
      return LABELS.pending;
  }
}

export function commitmentLabel(status?: string | null): string {
  return commitmentStatus(status).label;
}

export function isLockedIn(status?: string | null): boolean {
  return commitmentStatus(status).key === "locked_in";
}

export function hasBailed(status?: string | null): boolean {
  return commitmentStatus(status).key === "bailed";
}

/** Hours between now and the plan. Negative once the plan has started. */
export function hoursUntil(planAt: Date, now: Date = new Date()): number {
  return (planAt.getTime() - now.getTime()) / 3_600_000;
}

/**
 * Reconfirmation windows, aligned with the existing 24h/6h/1h reminder
 * scheduling plumbing. We only ask close to the plan (6h, then 1h).
 */
export type ReconfirmWindow = "6h" | "1h";

export function reconfirmWindow(
  planAt: Date,
  now: Date = new Date(),
): ReconfirmWindow | null {
  const h = hoursUntil(planAt, now);
  if (h <= 0) return null;
  if (h <= 1) return "1h";
  if (h <= 6) return "6h";
  return null;
}

const WINDOW_START_HOURS: Record<ReconfirmWindow, number> = { "6h": 6, "1h": 1 };

/**
 * Should we show the "Still solid?" prompt?
 *
 * Rules (deliberately anti-nag):
 *  - only for locked-in invitees,
 *  - only inside a 6h or 1h window before the plan,
 *  - never twice within the same window: a confirmation made after the window
 *    opened silences the prompt for that window.
 */
export function shouldPromptReconfirm(opts: {
  status?: string | null;
  planAt: Date;
  lastConfirmedAt?: string | Date | null;
  now?: Date;
}): boolean {
  const now = opts.now ?? new Date();
  if (!isLockedIn(opts.status)) return false;

  const window = reconfirmWindow(opts.planAt, now);
  if (!window) return false;

  if (!opts.lastConfirmedAt) return true;

  const confirmedAt =
    typeof opts.lastConfirmedAt === "string"
      ? new Date(opts.lastConfirmedAt)
      : opts.lastConfirmedAt;
  if (Number.isNaN(confirmedAt.getTime())) return true;

  const windowOpenedAt = new Date(
    opts.planAt.getTime() - WINDOW_START_HOURS[window] * 3_600_000,
  );
  return confirmedAt < windowOpenedAt;
}

/** Human countdown pieces for a plan (not just a reveal). */
export function countdownParts(planAt: Date, now: Date = new Date()) {
  const diff = planAt.getTime() - now.getTime();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    minutes: Math.floor((diff % 3_600_000) / 60_000),
    seconds: Math.floor((diff % 60_000) / 1000),
  };
}

export function planProximityLabel(planAt: Date, now: Date = new Date()): string {
  const h = hoursUntil(planAt, now);
  if (h <= 0) return "Happening now";
  if (h < 1) return "Less than an hour away";
  if (h < 24) return `${Math.round(h)} hours away`;
  const days = Math.floor(h / 24);
  return `${days} day${days > 1 ? "s" : ""} away`;
}

/**
 * Privacy-safe aggregate for hosts. Individual bailers are never named here;
 * we only ever surface counts, and we keep the established rule that the
 * detailed bail breakdown belongs to the reveal moment.
 */
export function commitmentAggregate(statuses: Array<string | null | undefined>) {
  const total = statuses.length;
  const lockedIn = statuses.filter(isLockedIn).length;
  const bailed = statuses.filter(hasBailed).length;
  const pending = total - lockedIn - bailed;
  return { total, lockedIn, bailed, pending: Math.max(0, pending) };
}

/** Copy used after a bail — recovery framing, never stigma. */
export const RECOVERY_COPY =
  "Reliability rebuilds itself — lock in on your next plan and follow through.";

/** Copy explaining what actually moves reliability. */
export const RELIABILITY_COPY =
  "Locking in honestly, reconfirming when we check in, and actually showing up is what strengthens your reliability and streaks.";

/**
 * Commitment-oriented share copy. The viral loop here is "come lock in with
 * me", not a generic referral — no rewards, no spam.
 */
export function commitmentShareText(opts: {
  planTitle: string;
  isRevealed?: boolean;
  allBailed?: boolean;
  countdown?: { days: number; hours: number; minutes: number } | null;
}): string {
  const { planTitle, isRevealed, allBailed, countdown } = opts;
  if (isRevealed) {
    return allBailed
      ? `👻 Everyone bailed on "${planTitle}" — the secret is out!`
      : `🔓 "${planTitle}" has been revealed on BailBuddies!`;
  }
  const when = countdown
    ? ` Reveal in ${countdown.days}d ${countdown.hours}h ${countdown.minutes}m.`
    : "";
  return `🔒 Lock in with me for "${planTitle}" on BailBuddies — say you'll show up, with bail choices hidden before the reveal.${when}`;
}

/** Short CTA used next to share buttons. */
export const SHARE_CTA_COPY = "Invite a friend to lock in with you";

/**
 * Precise privacy wording. Bailing is private *before* the reveal; what is
 * visible afterwards is governed by the drop's existing reveal rules, so we
 * never promise permanent anonymity.
 */
export const PRIVACY_BEFORE_REVEAL_COPY =
  "Your bail stays hidden from other guests before the reveal — host visibility and anything shown later follow the plan's reveal rules.";

/** Short inline variant for tight UI spots. */
export const PRIVACY_SHORT_COPY = "Hidden from other guests before the reveal";
